// 3rd Party
import angular, { IWindowService } from 'angular';
import 'angular-filter';
import 'angular-rangeslider';
import 'angular-sanitize';
import 'angular-touch';
import 'angular-ui-bootstrap';
import {
    StateParams,
    Transition,
    TransitionService,
    UIRouter,
} from 'angular-ui-router';
import 'angular-validation-match';
import 'ngstorage';

declare global {
    const SENTRY_TRACE_SAMPLE_RATE: number;
}

// local angular modules
import tnAccount from '../tn.account/module.tn.account';
import tnBilling from '../tn.billing/module.tn.billing';
import tnCart from '../tn.cart/module.tn.cart';
import tnCommon, {
    DEFAULT_DESCRIPTION,
    OrderFilters,
    standardizeUrl,
} from '../tn.common';
import tnConfirmation from '../tn.confirmation/module.tn.confirmation';
import tnCore from '../tn.core';
import tnDjango from '../tn.django';
import tnFavorites from '../tn.favorites/module.tn.favorites';
import tnFonts from '../tn.fonts/tn.fonts';
import tnLicenses from '../tn.licenses/module.tn.licenses';
import tnPopover from '../tn.popover/module.tn.popover';
import tnPricing from '../tn.pricing/module.tn.pricing';
import tnProjects from '../tn.projects/module.tn.projects';
import tnStripe from '../tn.stripe/module.tn.stripe';
import tnUi from '../tn.ui/module.tn.ui';
import tnUser from '../tn.user/module.tn.user';
import tnUtils from '../tn.utils/module.tn.utils';

// local components
import { UserService } from '../tn.user/service.tn.user';
import { TnStoreErrorHandlingConfig } from './tn.app.ErrorHandlingConfig';
import { TNStateDeclaration, TnStoreRouteConfig } from './tn.app.routeConfig';

import * as Sentry from '@sentry/browser';
import { Angular as AngularIntegration } from '@sentry/integrations';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import { IRootScope } from '../tn.common/classes/Model/RootScope';
import {
    accountOrdersPageComponentOptions,
    accountOrdersSidebarComponentOptions,
    CatalogPageComponent,
    CatalogSidebarComponent,
    FamilyPageComponent,
    FamilySidebarComponent,
    SeriesPageComponent,
    SeriesPageSidebarComponent,
    StylePageComponent,
    StylePageSidebarComponent,
} from './pages';
import { runTitleUpdates } from './run/runTitleUpdates';
import {
    CachingHttpInterceptorFactory,
    HttpCacheConfig,
} from './tn.app.httpCachingConfig';

// Make sure to call Sentry.init after importing AngularJS.
// You can also pass {angular: AngularInstance} to the Integrations.Angular() constructor.
const sentryOptions = {
    dsn: 'SENTRY_DSN',
    release: 'SENTRY_RELEASE',
    environment: 'SENTRY_ENVIRONMENT',
    integrations: [
        new AngularIntegration({ angular }),
        // TODO: FIX API CORS: update Access-Control-Allow-Headers on API response to allow sentry-trace header
        //
        new TracingIntegrations.BrowserTracing({
            // tracingOrigins: [
            //     'api.typenetwork.com',
            //     'cloud.typenetwork.com',
            //     'localhost',
            //     'localhost:8000',
            // ],
        }),
    ],
    tracesSampleRate: SENTRY_TRACE_SAMPLE_RATE,
};
Sentry.init(sentryOptions);

// This is the entry point to the app.  This is a wrapper module that takes care of routing, etc.

function updatePageDescription(content = DEFAULT_DESCRIPTION) {
    angular.element('head meta[name="description"]').attr('content', content);
}

TnAppRun.$inject = [
    '$rootScope',
    '$transitions',
    '$uiRouter',
    '$window',
    'tn.user.service',
    'canonicalBaseUrl',
];
function TnAppRun(
    $rootScope: IRootScope,
    $transitions: TransitionService,
    $uiRouter: UIRouter,
    $window: IWindowService,
    userService: UserService,
    canonicalBaseUrl: string
) {
    function setWrapperClasses(toState, target) {
        const stateTerms = toState.name.split('.');
        stateTerms.shift();

        target.pageWrapperCssClasses = [];

        for (let i = 0; i < stateTerms.length; i++) {
            target.pageWrapperCssClasses.push(
                'template-' + stateTerms.slice(0, i + 1).join('-')
            );
        }

        if (
            toState.data &&
            toState.data.bodyClasses &&
            toState.data.bodyClasses.length > 0
        ) {
            for (let i = 0; i < toState.data.bodyClasses.length; i++) {
                target.pageWrapperCssClasses.push(toState.data.bodyClasses[i]);
            }
        }

        //Set sidebar properties for ng-class
        if (toState.data && toState.data.sidebar) {
            target.pageWrapperCssClasses.push(
                'layout_sidebar' + toState.data.sidebar.type
            );
        }
    }

    $uiRouter.stateService.defaultErrorHandler(function (error) {
        console.error(error.message, error);
        // I can't pass the error directly here, so copy into a new object
        $uiRouter.stateService.go(
            'home.error',
            {
                error: {
                    message: error.message,
                    name: error.name,
                    type: error.type,
                    data: error.data,
                },
            },
            {
                location: false,
            }
        );
    });

    // protect authorized pages.
    $transitions.onBefore({}, function (transition: Transition) {
        // TODO: move this to the pages instead of the router so it doesn't hold up every route transition.
        const to = transition.to() as TNStateDeclaration;
        const stateName = to.name;
        const requireAuthorized = !!to.data?.requireAuthorized;
        console.log('transition.onBefore', {
            stateName,
            requireAuthorized,
            to,
        });
        // don't catch on the oAuthRedirectUri
        if (stateName == 'oauthRedirectUri') return;
        // if auth isn't required, don't do anything.
        if (!requireAuthorized) return;
        return userService.ready
            .then(() => {
                const isLoggedIn = userService.isLoggedIn();
                console.log(
                    'TnAppRun.transitions.onBefore, userService.ready',
                    { isLoggedIn }
                );

                // this will transition for login is complete...
                if (!isLoggedIn) {
                    const stateParams = transition.params() as StateParams;
                    console.log(
                        'authorized required, but not logged in',
                        stateName,
                        stateParams
                    );
                    // this will redirect to the login page, and then back to the state.
                    userService.oauthLogin(stateName, stateParams);
                }
            })
            .catch((error) => {
                console.log(
                    'TnAppRun.transitions.onBefore, userService.ready.catch',
                    error
                );
            });
    });

    $transitions.onFinish({}, function (transition) {
        const tokens = transition.getResolveTokens();
        const foundry = tokens.find((token) => token === 'foundry')
            ? transition.injector().get('foundry')
            : null;
        $rootScope.headerFoundry = foundry;
    });

    $transitions.onSuccess({}, function (transition) {
        // on success is called on all param changes, dynamic or otherwise.
        // so this should handle anything that needs to happen on any url update,
        // including ones that don't result in a state change.
        const toState = transition.to();
        setWrapperClasses(toState, $rootScope);

        try {
            const description = transition.injector().get('$description');
            updatePageDescription(description);
        } catch (error) {
            updatePageDescription();
        }

        const canonicalUrl = `${canonicalBaseUrl}${transition.router.urlService.path()}`;
        const canonicalLink = $window.document.querySelector(
            'link[rel="canonical"]'
        );
        canonicalLink?.setAttribute('href', canonicalUrl);
    });

    $transitions.onEnter({}, () => {
        // onEnter is only called when entering a new state, not when dynamic params change.
        // scroll to top
        window.scrollTo(0, 0);
        // delete any hanging popovers that were open, and might have come with us.
        // TODO: investigate using popover service to close all popovers instead.
        angular.element('body>.popover').remove();
    });

    angular.element($window).click(($e) => {
        // clear sidebar select.
        const parentSelect = angular.element($e.target).parents('.select');
        angular.element('#sidebar .select.is_active').each((i, select) => {
            if (select !== parentSelect[0]) {
                angular.element(select).triggerHandler('click');
            }
        });
    });
}

const baseUrl = window.location.origin;
const redirect_uri = standardizeUrl(baseUrl, '/oidc/callback');
const post_logout_redirect_uri = standardizeUrl(
    baseUrl,
    '/oidc/post-logout-redirect'
);
console.log('store.typenetwork.com: initializing');
export default angular
    .module('tn.app', [
        // 3rd Party Modules
        'angular.filter',
        'ngSanitize',
        'ngSentry',
        'ngStorage',
        'ngTouch',
        'ui-rangeSlider',
        'ui.router',

        // Local Modules
        tnCommon,
        tnAccount,
        tnBilling,
        tnCore,
        tnCart,
        tnConfirmation,
        tnDjango,
        tnFavorites,
        tnFonts,
        tnLicenses,
        tnPopover,
        tnPricing,
        tnProjects,
        tnStripe,
        tnUi,
        tnUser,
        tnUtils,
    ])

    // accountOrderd Page Specific resources.
    .component('accountOrdersPage', accountOrdersPageComponentOptions)
    // This side bars is to only be used with the accountsOrderPage. Long term,
    // we need top stop using the @header/@sidebar views in UI router and only
    // represent pages as a single component so the sidebars and communicate directly
    // with the page without the need for intermediary services like accountOrderFilters
    .component('accountOrdersSidebar', accountOrdersSidebarComponentOptions)
    // configuration constants, injected by rollup-replace from env
    // then injected into other providers.
    .constant('tnAuthIssuer', 'TN_AUTH_ISSUER')
    .constant('tnAuthClientId', 'TN_AUTH_CLIENT_ID')
    .constant('tnAuthRedirectUri', redirect_uri)
    .constant('tnAuthPostLogoutRedirectUri', post_logout_redirect_uri)
    .constant('tnAuthExtraOptions', {})
    .constant('canonicalBaseUrl', 'TN_CANONICAL_BASEURL')
    // run configuration classes.
    // family catalog is /fonts, /foundry/:foundryName/fonts, it is a list of families
    .component('catalogPage', CatalogPageComponent)
    .component('catalogPageSidebar', CatalogSidebarComponent)

    // foundry/:foundry/fonts/:familyName
    .component('familyPage', FamilyPageComponent)
    .component('familyPageSidebar', FamilySidebarComponent)

    // foundry/:foundry/series/:seriesName
    .component('seriesPage', SeriesPageComponent)
    .component('seriesPageSidebar', SeriesPageSidebarComponent)

    .component('stylePage', StylePageComponent)
    .component('stylePageSidebar', StylePageSidebarComponent)

    // this service instance is intended to be used exclusively for the accountOrdersSidebar
    // to communicate with the accountOrdersPage.
    .service('accountOrderFilters', OrderFilters)
    .factory('CachingHttpInterceptor', CachingHttpInterceptorFactory)

    // Config and launch
    .config(TnStoreErrorHandlingConfig)
    .config(TnStoreRouteConfig)
    .config(HttpCacheConfig)
    .run(runTitleUpdates)
    .run(TnAppRun).name;
